// Contents list for policy pages. snippets/aplovio-policy-assets.liquid loads this file only on policy pages, and
// only with the theme setting policy_contents; the script tag carries the translated label. Shopify renders the
// policy itself, so the sections are found in its markup: the h2 headings, else the h3 headings, else lines that
// are only bold text followed by a line break (how Shopify's Terms of service template starts each section).
// Fewer than three sections: no list. The policy text is never changed; only the list shows a heading written in
// capitals in sentence case.
(() => {
  const label = document.currentScript?.dataset.policyContents || 'Contents';
  const start = () => {
    const container = document.querySelector('.shopify-policy__container');
    const body = container?.querySelector('.shopify-policy__body');
    if (!body || container.querySelector('.ap-policy-contents')) return;
    const visible = (node) => node.textContent.trim() !== '';
    let headings = [...body.querySelectorAll('h2')].filter(visible);
    if (headings.length < 3) headings = [...body.querySelectorAll('h3')].filter(visible);
    if (headings.length < 3) {
      const near = (node, side) => {
        let next = node[side];
        while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) next = next[side];
        return next;
      };
      const lineBreak = (node) => !node || node.nodeName === 'BR';
      headings = [...body.querySelectorAll('strong, b')].filter((bold) => {
        const text = bold.textContent.trim();
        if (text.length < 2 || text.length > 120 || !['P', 'DIV'].includes(bold.parentElement.nodeName)) return false;
        const before = near(bold, 'previousSibling'), after = near(bold, 'nextSibling');
        return lineBreak(before) && after?.nodeName === 'BR';
      });
      if (headings.length < 3) return;
      headings.forEach((bold) => bold.classList.add('ap-policy-label'));
    }

    const lang = document.documentElement.lang || undefined;
    const capitals = (text) => {
      const letters = text.match(/\p{L}/gu) || [];
      return letters.length >= 4 && letters.filter((letter) => letter !== letter.toLocaleLowerCase(lang)).length / letters.length >= 0.6;
    };
    const entry = (text) => {
      const line = text.trim().replace(/\s+/g, ' ');
      return capitals(line) ? line.toLocaleLowerCase(lang).replace(/^\p{L}/u, (letter) => letter.toLocaleUpperCase(lang)) : line;
    };
    const slug = (text) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
    for (const heading of headings) {
      if (heading.id) continue;
      const base = slug(heading.textContent);
      let id = base;
      for (let n = 2; document.getElementById(id); n += 1) id = `${base}-${n}`;
      heading.id = id;
    }

    const nav = document.createElement('nav');
    nav.className = 'ap-policy-contents';
    nav.setAttribute('aria-label', label);
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    summary.textContent = label;
    const list = document.createElement('ol');
    const links = headings.map((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      link.textContent = entry(heading.textContent);
      item.append(link);
      list.append(item);
      return link;
    });
    details.append(summary, list);
    nav.append(details);
    container.insertBefore(nav, body);
    container.classList.add('ap-policy--contents');

    // From 990 px the list is a column that is always open; below, a row that opens on demand.
    const wide = matchMedia('(min-width: 990px)');
    const layout = () => {
      details.open = wide.matches;
      if (wide.matches) summary.tabIndex = -1;
      else summary.removeAttribute('tabindex');
    };
    layout();
    wide.addEventListener('change', layout);

    // The current section is the last one whose heading has passed under the header. At the end of the page, where
    // the last headings can no longer reach the top, it is the last heading on screen.
    let frame = 0;
    const mark = () => {
      frame = 0;
      const end = innerHeight + scrollY >= document.documentElement.scrollHeight - 2;
      const line = end ? innerHeight - 48 : (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ap-header-height')) || 0) + 48;
      let current = -1;
      headings.forEach((heading, index) => {
        if (heading.getBoundingClientRect().top <= line) current = index;
      });
      links.forEach((link, index) => {
        if (index === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (current >= 0 && wide.matches && list.scrollHeight > list.clientHeight) {
        const target = links[current].offsetTop - list.clientHeight / 2;
        if (Math.abs(list.scrollTop - target) > list.clientHeight / 3) list.scrollTop = Math.max(0, target);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(mark);
    };
    addEventListener('scroll', schedule, {passive: true});
    addEventListener('resize', schedule, {passive: true});
    mark();

    // A link to a section opened from elsewhere: the ids exist only now, so go to it, and again once fonts and
    // images have loaded above it, unless the visitor has started moving.
    const target = location.hash.length > 1 && document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target && headings.includes(target)) {
      let moved = false;
      const stop = () => {
        moved = true;
      };
      for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) addEventListener(type, stop, {once: true, passive: true});
      const go = () => {
        if (!moved) target.scrollIntoView();
      };
      go();
      if (document.readyState !== 'complete') addEventListener('load', go, {once: true});
      document.fonts?.ready.then(go);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
